import * as path from 'path';

import { Vector3 } from 'three';
import type * as zod from 'zod';

import type { Plane } from '@sb/geometry';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import type { WristCamera } from '@sb/integrations/implementations/WristCamera/implementation/WristCamera';
import type {
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';
import * as log from '@sb/log';
import type { ArmJointPositions } from '@sb/motion-planning';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { Space } from '@sb/routine-runner/types';
import { resolveLocateGpuTaskRunner } from '@sb/routine-runner/vision/helpers';
import type { LocateResult } from '@sb/routine-runner/vision/LocateTypes';
import { runLocate } from '@sb/routine-runner/vision/runLocate';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { loadHostIdentity } from '@sb/service-interfaces/host/identity';
import { six } from '@sb/utilities';

import type { ImageValidationFailure } from '../Capture/captureUtils';
import {
  formatCaptureTimestamp,
  saveImageToStorage,
  saveMetadataToStorage,
} from '../Capture/captureUtils';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments, { PlaneDetectionModeEnum } from './Arguments';
import Variables from './Variables';

const ns = log.namespace('locateStep');

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

/**
 * We save a dummy set of joint angles for each position. Calculation of the joint angles
 * to use to reach the position should be deferred until a move arm step is played
 */
const NULL_JOINT_ANGLES: ArmJointPositions = six(0);

export default class LocateStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      latestResult: null,
      resultCount: 0,
      latestSaveFilepath: null,
      captureTimestamp: null,
      fullResults: null,
    };
  }

  private getPlaneSpaceItem(): Space.Plane {
    if (!this.args.planeID) {
      throw new Error('No planeID provided');
    }

    const plane = this.routineFrame.getSpaceItem(this.args.planeID);

    if (plane.kind !== 'plane') {
      throw new Error(`SpaceItem ${this.args.planeID} is not a plane`);
    }

    return plane;
  }

  private getPlaneFromSpaceItemID(): Plane {
    const plane = this.getPlaneSpaceItem();

    const planePositions = plane.positions.map(
      (position) =>
        new Vector3(position.pose.x, position.pose.y, position.pose.z),
    );

    return {
      origin: planePositions[0],
      plusX: planePositions[1],
      plusY: planePositions[2],
    };
  }

  /**
   * If `captureConfig` is set and the `captureStep` feature flag is enabled,
   * saves the locate image to storage and sets capture-related variables.
   *
   * Returns an `ImageValidationFailure` when the image fails validation
   * (caller should pass it to `fail`), or `null` on success / when capture
   * is not applicable.
   */
  private async maybeCaptureImage(
    image: ArrayBuffer,
    locateResults?: LocateResult[],
  ): Promise<ImageValidationFailure | null> {
    const { captureConfig } = this.args;

    if (!captureConfig) {
      return null;
    }

    // Set timestamp before evaluating expression (it may reference it)
    const formattedTimestamp = formatCaptureTimestamp(new Date());
    this.setVariable('captureTimestamp', formattedTimestamp);

    // Evaluate the filepath expression to get the dynamic filename
    const remotePath = await this.routineFrame.evaluateExpression(
      captureConfig.saveFilepath,
    );

    if (typeof remotePath !== 'string') {
      throw new Error(
        `Filepath expression must evaluate to a string, received ${typeof remotePath}`,
      );
    }

    if (!remotePath) {
      throw new Error(
        'Filepath expression must evaluate to a non-empty string',
      );
    }

    if (!remotePath.endsWith('.png')) {
      throw new Error(`Filepath must end with .png (Filepath: ${remotePath})`);
    }

    // Get the storage equipment (FTP Server or S3 Bucket)
    const storageEquipment = this.routineContext.equipment.getDeviceById(
      captureConfig.saveEquipmentId,
    );

    if (!storageEquipment) {
      throw new Error(
        `Storage equipment not found: ${captureConfig.saveEquipmentId}`,
      );
    }

    // Validate, convert to PNG, upload, and clean up
    const validationFailure = await saveImageToStorage({
      image,
      remotePath,
      storageEquipment,
    });

    if (validationFailure) {
      return validationFailure;
    }

    this.setVariable('latestSaveFilepath', remotePath);

    await this.maybeSaveMetadata(remotePath, storageEquipment, locateResults);

    log.info(ns`locate.capture`, 'Locate step capture completed', {
      remotePath,
    });

    return null;
  }

  /**
   * If `saveMetadata` is enabled, builds a metadata object for the captured
   * image and uploads it alongside the image as a JSON sidecar file.
   */
  private async maybeSaveMetadata(
    remotePath: string,
    storageEquipment: { kind: string; executeCommand: (...args: any[]) => any },
    locateResults?: LocateResult[],
  ): Promise<void> {
    if (!this.args.captureConfig?.saveMetadata) {
      return;
    }

    const identity = await loadHostIdentity();

    const { captureConfig: _captureConfig, ...stepParameters } = this.args;

    await saveMetadataToStorage({
      metadata: {
        imageFilename: path.basename(remotePath),
        botId: this.routineContext.robotId,
        botAlias: identity?.alias,
        timestamp: new Date().toISOString(),
        stepKind: 'Locate',
        locateResults,
        stepParameters,
      },
      remotePath,
      storageEquipment,
    });
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    try {
      log.info(ns`locate.play`, 'Playing Locate step');

      // Use type assertion since useCameraBotCpp may not be in the feature flag types yet
      const useCameraBotCpp = getFeatureFlagSync('useCppCameraBot', false);

      let cameraEquipment: Camera | WristCamera | undefined;
      let accuracyCalibration: Space.AccuracyCalibrationEntry[] | null = null;
      let intrinsics: CameraIntrinsicsExtended | undefined;
      let extrinsics: ExtrinsicsKinematics | undefined;

      if (useCameraBotCpp) {
        // camera-bot-cpp is enabled, use camera equipment ID
        if (!this.args.cameraEquipmentId) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No camera equipment ID provided',
          });
        }

        // Use new Camera implementation from camera-bot
        // If cameraEquipmentId is set, use that specific camera
        cameraEquipment = this.routineContext.equipment.getDeviceById(
          this.args.cameraEquipmentId,
        ) as Camera | undefined;

        if (!cameraEquipment) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment with id ${this.args.cameraEquipmentId} not found`,
          });
        }

        const planeDetectionMode = this.args.planeDetectionMode ?? 'Manual';
        const isManualMode = planeDetectionMode === 'Manual';

        const cameraConfig = cameraEquipment.getConfig() as CameraConfiguration;
        const { extrinsicsId } = cameraConfig;
        const { intrinsicsId } = cameraConfig;

        if (extrinsicsId == null || intrinsicsId == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no extrinsics or intrinsics set.`,
          });
        }

        extrinsics = cameraEquipment.getExtrinsics(true);

        intrinsics =
          cameraConfig.intrinsicsData[intrinsicsId].intrinsics ?? undefined;

        if (extrinsics == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no extrinsics set.`,
          });
        }

        if (intrinsics == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no intrinsics set.`,
          });
        }

        if (isManualMode) {
          const plane = this.getPlaneSpaceItem();

          const accuracyCalibrationFull = Object.values(
            plane.cameraAccuracyCalibrations,
          ).find(
            (calibration) =>
              calibration.equipmentId === this.args.cameraEquipmentId &&
              calibration.extrinsicsId === extrinsicsId,
          );

          if (accuracyCalibrationFull != null) {
            accuracyCalibration = accuracyCalibrationFull.calibrationEntries;
          } else {
            log.info(
              ns`locate.play`,
              "No accuracy calibration found for the selected camera's active extrinsics.",
              {
                cameraEquipmentId: this.args.cameraEquipmentId,
                extrinsicsId,
              },
            );
          }
        }
      } else {
        // camera-bot-cpp is not enabled, use wrist camera
        cameraEquipment =
          this.routineContext.equipment.getWristCamera() ?? undefined;

        if (!cameraEquipment) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No wrist camera found.',
          });
        }

        if ((this.args.planeDetectionMode ?? 'Manual') === 'Manual') {
          accuracyCalibration = this.getPlaneSpaceItem().calibrationEntries;
        }
      }

      const planeDetectionMode = this.args.planeDetectionMode ?? 'Manual';

      if (!useCameraBotCpp && planeDetectionMode !== 'Manual') {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: `${planeDetectionMode} plane detection requires camera-bot-cpp. Enable the useCppCameraBot feature flag.`,
        });
      }

      // Only resolve plane geometry for Manual mode
      const plane =
        planeDetectionMode === 'Manual'
          ? this.getPlaneFromSpaceItemID()
          : undefined;

      // Capture depth frame for Geometric/Learned modes and Detection3D
      let depthImage: ArrayBuffer | undefined;

      const needsDepth =
        planeDetectionMode === PlaneDetectionModeEnum.enum.Geometric ||
        planeDetectionMode === PlaneDetectionModeEnum.enum.Learned ||
        this.args.method.kind === 'Detection3D';

      if (needsDepth) {
        try {
          depthImage = await cameraEquipment.getDepthFrame();
        } catch (err) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Failed to capture depth frame: ${err instanceof Error ? err.message : String(err)}`,
          });
        }
      }

      // Honor the method's own GPU selection ("" = "None (CPU only)") for
      // every method kind — do not fall back to the first healthy GPU box the
      // user never selected (it may run a different software version than the
      // local vision-bot, and plane projection must run where the user chose).
      const gpuTaskRunner = resolveLocateGpuTaskRunner(
        this.routineContext.equipment,
        this.args.method,
        'locate.gpuUnhealthy',
      );

      const { results: runLocateResults, image } = await runLocate({
        camera: {
          equipment: cameraEquipment,
          integration: this.args.camera,
        },
        regionOfInterest: this.args.regionOfInterest,
        method: this.args.method,
        plane,
        objectHeight: this.args.objectHeight,
        resultsLimit: this.args.filters.resultsLimit,
        transform: this.args.transform,
        accuracyCalibration,
        vision: this.routineContext.vision,
        getState: () => this.routineContext.getRoutineRunnerState(),
        equipment: this.routineContext.equipment,
        intrinsics,
        extrinsics,
        gpuTaskRunner,
        planeDetectionMode,
        ransacParams: this.args.ransacParams,
        resultOrdering: this.args.resultOrdering,
        depthImage,
        maxTiltAngle: this.args.maxTiltAngle,
        disablePlaneOrientation: this.args.disablePlaneOrientation,
      });

      const positions: Space.Position[] = runLocateResults.map((result) => {
        return {
          pose: result.pose,
          jointAngles: NULL_JOINT_ANGLES,
          tcpOption: 'ee-any',
        };
      });

      this.setVariable('latestResult', positions);
      this.setVariable('resultCount', positions.length);
      this.setVariable('fullResults', runLocateResults);

      this.routineFrame.setPositionListEntries(
        this.args.positionListID,
        positions,
      );

      // Optionally save the captured image (feature-flag gated)
      const captureFailure = await this.maybeCaptureImage(
        image,
        runLocateResults,
      );

      if (captureFailure) {
        return fail(captureFailure);
      }
    } catch (error) {
      log.error(ns`locate.play`, 'Locate failed', error);

      if ((error as any).failureKind === FailureKind.CameraFailure) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: error.message,
          error,
        });
      }

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Locate',
        },
        failureReason: `${error.message}`,
        error,
      });
    }
  }
}
